import { Link } from 'react-router-dom'
import { useSwipeNavigateOnMobile } from '@/hooks/useSwipeNavigateOnMobile'
import { SwipeRevealTradingCardShell } from '@/components/trading/SwipeRevealTradingCardShell'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MinimalPriceChart from '@/components/ui/MinimalPriceChart'
import FavoriteControl from '@/components/trading/FavoriteControl'
import TradeResultPanel from '@/components/trading/TradeResultPanel'
import TradeTypeBadges from '@/components/trading/TradeTypeBadges'
import { faChartArea, faSpinner, faChevronDown, faChevronUp } from '@/fontawesome'
import type { RecommendationSwingtrade, RecommendationFavorite } from '@/models'
import type { GraphPoint } from '@/modelsAPI'
import { formatCurrencyCentValue, formatDate, formatDelta } from '@/utils/helpers'
import { TRADING_ROUTES } from '@/constants/routes'
import { getStateLabel, getStateBadgeColor, isFinishedTrade } from '@/utils/trading-recommendation-helpers'

interface SwingTradeCardProps {
  trade: RecommendationSwingtrade & {
    graphPoints?: GraphPoint[]
    graphLoading?: boolean
  }
  expanded?: boolean
  onToggle?: () => void
  onFavoriteChange?: (favorite: RecommendationFavorite | null) => void
  detailView?: boolean
}

export default function SwingTradeCard({ trade, expanded = false, onToggle, onFavoriteChange, detailView = false }: SwingTradeCardProps) {
  const currency = trade.symbol?.currentData?.currency ?? 'USD';
  const sortedAdjustments = [...trade.adjustements].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )

  // Get latest adjustment for mobile compact view
  const latestAdjustment = sortedAdjustments[0]

  // Check if trade is finished
  const isFinished = isFinishedTrade(trade)

  const swipeRef = useSwipeNavigateOnMobile(TRADING_ROUTES.SWING_TRADE_DETAIL(trade.id))

  return (
    <SwipeRevealTradingCardShell
      swipeRef={swipeRef}
      surfaceAriaLabel="Zum Detail nach rechts wischen; auf größeren Bildschirmen Chart anklicken"
      surfaceClassName={`touch-pan-y bg-white dark:bg-gray-800 rounded-lg overflow-hidden ${
      trade.boost
        ? 'border-2 border-amber-400 dark:border-amber-500 shadow-lg shadow-amber-200/50 dark:shadow-amber-900/30'
        : 'border border-gray-200 dark:border-gray-700'
    }`}
    >
      {/* Favorite control (top right) */}
      <FavoriteControl
        type="SWING"
        recommendationId={trade.id}
        favorite={trade.favorite}
        onChange={(fav) => onFavoriteChange?.(fav)}
        className="absolute top-2 right-2 z-20"
      />

      {/* Main Info Section */}
      <div className="p-3 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-6">
          {/* Chart - hidden on mobile */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="h-32">
              {trade.graphLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-gray-400 animate-spin" />
                </div>
              ) : trade.graphPoints && trade.graphPoints.length > 0 ? (
                <Link to={TRADING_ROUTES.SWING_TRADE_DETAIL(trade.id)} className="block cursor-pointer">
                  <MinimalPriceChart
                    points={trade.graphPoints}
                    strikePrice={(isFinished ? trade.averageBuyPrice : trade.averagePrice) / 100}
                    height={128}
                    adjustements={trade.adjustements}
                    averagePrice={(isFinished ? trade.averageBuyPrice : trade.averagePrice) / 100}
                    takeProfit={trade.takeProfit > 0 ? trade.takeProfit / 100 : undefined}
                  />
                </Link>
              ) : (
                <Link to={TRADING_ROUTES.SWING_TRADE_DETAIL(trade.id)} className="block cursor-pointer w-full h-full">
                  <div className="w-full h-full bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                    <FontAwesomeIcon
                      icon={faChartArea}
                      className="w-12 h-12 text-gray-400 dark:text-gray-500"
                    />
                  </div>
                </Link>
              )}
            </div>
          </div>

          {/* Main Details */}
          <div className="lg:col-span-9 space-y-3 lg:space-y-4">
            {/* First Row */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-4">
              {/* Symbol with Chevron */}
              <div className="lg:col-span-1 min-w-0">
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Symbol</div>
                <div className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 min-w-0">
                  <span className="inline-flex flex-wrap min-w-0">
                    {trade.stockExchange?.identifier !== null && trade.stockExchange?.identifier !== undefined
                      ? <span>{trade.stockExchange.identifier}:</span>
                      : null}
                    <span>{trade.symbolTicker}</span>
                  </span>
                  {/* Chevron for Mobile Expand/Collapse */}
                  {onToggle && (
                    <button
                      onClick={onToggle}
                      className="lg:hidden flex-shrink-0 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer transition-colors"
                      aria-label={expanded ? "Einklappen" : "Ausklappen"}
                    >
                      <FontAwesomeIcon
                        icon={expanded ? faChevronUp : faChevronDown}
                        className="w-4 h-4"
                      />
                    </button>
                  )}
                </div>
                <TradeTypeBadges type={trade.type} boost={trade.boost} free={trade.free} />
              </div>

              {/* Letzter Einstieg - nur bei offenen Trades */}
              {!isFinished && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Letzter Einstieg</div>
                  {/* Mobile: Show % and Price from latest adjustment */}
                  <div className="lg:hidden">
                    {latestAdjustment && (
                      <>
                        <div className="text-base text-gray-900 dark:text-white">
                          <span className={`font-medium ${
                            latestAdjustment.delta >= 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {formatDelta(latestAdjustment.delta)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatCurrencyCentValue(latestAdjustment.price, currency)}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Desktop: Show full date & time */}
                  <div className="hidden lg:block">
                    <div className="text-base text-gray-900 dark:text-white">
                      {formatDate(trade.lastAdjustement).split(' ')[0].replaceAll(',', '')}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(trade.lastAdjustement).split(' ')[1]}
                    </div>
                  </div>
                </div>
              )}

              {/* Branche - hidden on mobile */}
              <div className="hidden lg:block">
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Branche</div>
                <div className="text-base text-gray-900 dark:text-white truncate" title={trade.symbol?.sector ?? ''}>
                  {trade.symbol?.sector ?? '-'}
                </div>
              </div>

              {/* Bedingte Anzeige basierend auf Trade-Status */}
              {isFinished ? (
                <>
                  {/* Bei beendeten Trades: Ø-Einstieg, Ø-Ausstieg, Rendite (mobil in Detail-Ansicht im Ergebnis-Panel) */}
                  <div className={`${expanded && !detailView ? 'flex flex-col gap-3' : 'hidden'} lg:contents`}>
                    <div className="hidden lg:block">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ø-Einstieg</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrencyCentValue(trade.averageBuyPrice, currency)}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ø-Ausstieg</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {trade.averageExitPrice !== null ? formatCurrencyCentValue(trade.averageExitPrice, currency) : '-'}
                      </div>
                    </div>
                  </div>

                  <div className={`${detailView ? 'hidden' : 'block'} lg:block`}>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Rendite</div>
                    <div className={`text-lg font-semibold ${
                      trade.yield >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {trade.yield >= 0 ? '+' : ''}{(trade.yield * 100).toFixed(2)}%
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Bei offenen Trades: Ø-Preis, Take-Profit, Gewinn/Verlust */}
                  <div className="hidden lg:block">
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ø-Preis</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatCurrencyCentValue(trade.averagePrice, currency)}
                    </div>
                  </div>

                  {trade.takeProfit > 0 && (
                    <div className={`${expanded ? 'block' : 'hidden'} lg:block`}>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Take-Profit</div>
                      <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {formatCurrencyCentValue(trade.takeProfit, currency)}
                      </div>
                    </div>
                  )}

                  <div className={`${expanded ? 'block' : 'hidden'} lg:block`}>
                    {(() => {
                      const profitLoss = trade.winLossPercentage;
                      const isProfit = profitLoss >= 0;

                      return (
                        <>
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Gewinn/Verlust</div>
                          <div className={`text-lg font-semibold ${
                            isProfit
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {isProfit ? '+' : ''}{(profitLoss * 100).toFixed(2)}%
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </>
              )}

              {/* Status - mobile: conditional (Label bündig zum Symbol-Label, Pill mittig), desktop: always visible */}
              <div className={`${(expanded || detailView) ? 'flex flex-col' : 'hidden'} lg:block`}>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Status</div>
                <div className="flex-1 flex items-center">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStateBadgeColor(trade)}`}>
                    {getStateLabel(trade)}
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Detail-Ansicht: Ergebnis-Panel mit Einstieg → Ausstieg und Rendite */}
            {isFinished && detailView && (
              <TradeResultPanel
                className="lg:hidden"
                averageBuyPrice={trade.averageBuyPrice}
                averageExitPrice={trade.averageExitPrice}
                yieldValue={trade.yield}
                currency={currency}
              />
            )}

            {/* Second Row - Desktop only, nur bei offenen Trades */}
            {!isFinished && (
              <div className="hidden lg:grid lg:grid-cols-6 gap-4">
                {/* Empty spacer for Symbol */}
                <div></div>

                {/* Empty spacer for Branche */}
                <div></div>

                {/* Aktueller Kurs - under Ø-Preis */}
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Aktueller Kurs</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {trade.symbol?.currentData?.price !== null && trade.symbol?.currentData?.price !== undefined
                      ? formatCurrencyCentValue(Math.round(trade.symbol.currentData.price * 100), currency)
                      : '-'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Einstiege Section - mobile: conditional, desktop: always visible */}
      <div className={`bg-gray-50 dark:bg-gray-900 px-3 py-3 lg:px-6 lg:py-4 border-t border-gray-200 dark:border-gray-700 ${expanded ? 'block' : 'hidden'} lg:block`}>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Einstiege ({trade.adjustements.length})
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Zeitstempel</th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">% des Depots</th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Preis</th>
                <th className="hidden lg:table-cell text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Laufende Position</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedAdjustments.map((adjustment, index) => {
                // Since sortedAdjustments are now newest-first, we need to calculate running position
                // by summing from current index to the end (oldest)
                const runningPosition = sortedAdjustments
                  .slice(index)
                  .reduce((sum, adj) => sum + adj.delta, 0)
                return (
                  <tr key={adjustment.id} className="hover:bg-gray-100 dark:hover:bg-gray-800">
                    <td className="py-2 px-3 text-gray-900 dark:text-white">
                      {formatDate(adjustment.timestamp)}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`font-medium ${
                        adjustment.delta >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {formatDelta(adjustment.delta)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-900 dark:text-white">
                      {formatCurrencyCentValue(adjustment.price, currency)}
                    </td>
                    <td className="hidden lg:table-cell py-2 px-3 font-medium text-gray-900 dark:text-white">
                      {(runningPosition * 100).toFixed(2)}%
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </SwipeRevealTradingCardShell>
  )
}
